import React, { useState, useEffect } from 'react';
import {
  Box,
  Stack,
  Typography,
  Card,
  CardContent,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Fab,
  IconButton,
  Tooltip,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  CalendarToday as CalendarIcon,
  Schedule as ScheduleIcon,
  Person as PersonIcon,
  Pets as PetsIcon,
  Cancel as CancelIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import rendezVousService, { RendezVous } from '../../services/rendezvousService';

interface MesRendezVousProps {
  onPrendreRendezVous?: () => void;
}

const MesRendezVous: React.FC<MesRendezVousProps> = ({
  onPrendreRendezVous
}) => {
  const [rendezVous, setRendezVous] = useState<RendezVous[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cancelDialog, setCancelDialog] = useState<{
    open: boolean;
    rdv: RendezVous | null;
  }>({ open: false, rdv: null });
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    loadRendezVous();
  }, []);

  const loadRendezVous = async () => {
    setLoading(true);
    try {
      const response = await rendezVousService.getRendezVous({
        page_size: 50
      });
      setRendezVous(response.results || []);
      setError(null);
    } catch (err) {
      setError('Erreur lors du chargement des rendez-vous');
      console.error('Erreur:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelRendezVous = async () => {
    if (!cancelDialog.rdv) return;

    setCancelling(true);
    try {
      await rendezVousService.annulerRendezVous(cancelDialog.rdv.id);
      await loadRendezVous(); // Recharger la liste
      setCancelDialog({ open: false, rdv: null });
    } catch (err) {
      setError('Erreur lors de l\'annulation du rendez-vous');
      console.error('Erreur:', err);
    } finally {
      setCancelling(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (statut: string) => {
    switch (statut) {
      case 'demande':
        return 'warning';
      case 'confirme':
        return 'success';
      case 'annule':
        return 'error';
      case 'termine':
        return 'info';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (statut: string) => {
    switch (statut) {
      case 'demande':
        return 'En attente';
      case 'confirme':
        return 'Confirmé';
      case 'annule':
        return 'Annulé';
      case 'termine':
        return 'Terminé';
      default:
        return statut;
    }
  };

  const canCancelRendezVous = (rdv: RendezVous) => {
    const rdvDate = new Date(rdv.date_heure);
    const now = new Date();
    const timeDiff = rdvDate.getTime() - now.getTime();
    const hoursDiff = timeDiff / (1000 * 3600);
    
    // Peut annuler si le RDV est dans plus de 24h et n'est pas déjà annulé ou terminé
    return hoursDiff > 24 && !['annule', 'termine'].includes(rdv.statut);
  };

  const isPastRendezVous = (rdv: RendezVous) => {
    const rdvDate = new Date(rdv.date_heure);
    const now = new Date();
    return rdvDate < now;
  };

  // Séparer les rendez-vous futurs et passés
  const futureRendezVous = rendezVous.filter(rdv => !isPastRendezVous(rdv));
  const pastRendezVous = rendezVous.filter(rdv => isPastRendezVous(rdv));

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" color="primary">
          Mes rendez-vous
        </Typography>
        <Stack direction="row" spacing={1}>
          <Tooltip title="Actualiser">
            <IconButton onClick={loadRendezVous} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {rendezVous.length === 0 ? (
        <Card>
          <CardContent>
            <Box textAlign="center" py={4}>
              <CalendarIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Aucun rendez-vous
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={3}>
                Vous n'avez pas encore de rendez-vous programmé.
              </Typography>
              {onPrendreRendezVous && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={onPrendreRendezVous}
                >
                  Prendre un rendez-vous
                </Button>
              )}
            </Box>
          </CardContent>
        </Card>
      ) : (
        <Stack spacing={3}>
          {/* Rendez-vous futurs */}
          {futureRendezVous.length > 0 && (
            <Box>
              <Typography variant="h6" gutterBottom color="primary">
                Rendez-vous à venir ({futureRendezVous.length})
              </Typography>
              <Stack spacing={2}>
                {futureRendezVous.map((rdv) => (
                  <Card key={rdv.id} elevation={2}>
                    <CardContent>
                      <Stack spacing={2}>
                        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                          <Box>
                            <Typography variant="h6" gutterBottom>
                              {formatDate(rdv.date_heure)}
                            </Typography>
                            <Box display="flex" alignItems="center" gap={1} mb={1}>
                              <ScheduleIcon fontSize="small" color="primary" />
                              <Typography variant="body1">
                                {formatTime(rdv.date_heure)} ({rdv.duree_minutes} min)
                              </Typography>
                            </Box>
                          </Box>
                          <Chip
                            label={getStatusLabel(rdv.statut)}
                            color={getStatusColor(rdv.statut) as any}
                            size="small"
                          />
                        </Box>

                        <Divider />

                        <Stack spacing={1}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <PersonIcon fontSize="small" color="action" />
                            <Typography variant="body2">
                              <strong>Vétérinaire :</strong> {rdv.veterinaire_nom || `Dr. Vétérinaire #${rdv.veterinaire}`}
                            </Typography>
                          </Box>
                          
                          <Box display="flex" alignItems="center" gap={1}>
                            <PetsIcon fontSize="small" color="action" />
                            <Typography variant="body2">
                              <strong>Animal :</strong> {rdv.animal_nom || `Animal #${rdv.animal}`}
                            </Typography>
                          </Box>
                          
                          <Box>
                            <Typography variant="body2">
                              <strong>Motif :</strong> {rdv.motif}
                            </Typography>
                          </Box>
                          
                          {rdv.notes_proprietaire && (
                            <Box>
                              <Typography variant="body2">
                                <strong>Notes :</strong> {rdv.notes_proprietaire}
                              </Typography>
                            </Box>
                          )}
                        </Stack>

                        {canCancelRendezVous(rdv) && (
                          <Box display="flex" justifyContent="flex-end">
                            <Button
                              variant="outlined"
                              color="error"
                              size="small"
                              startIcon={<CancelIcon />}
                              onClick={() => setCancelDialog({ open: true, rdv })}
                            >
                              Annuler
                            </Button>
                          </Box>
                        )}
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            </Box>
          )}

          {/* Rendez-vous passés */}
          {pastRendezVous.length > 0 && (
            <Box>
              <Typography variant="h6" gutterBottom color="text.secondary">
                Historique ({pastRendezVous.length})
              </Typography>
              <Stack spacing={2}>
                {pastRendezVous.slice(0, 5).map((rdv) => (
                  <Card key={rdv.id} elevation={1} sx={{ opacity: 0.8 }}>
                    <CardContent>
                      <Stack spacing={1}>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Typography variant="subtitle1">
                            {formatDate(rdv.date_heure)} - {formatTime(rdv.date_heure)}
                          </Typography>
                          <Chip
                            label={getStatusLabel(rdv.statut)}
                            color={getStatusColor(rdv.statut) as any}
                            size="small"
                            variant="outlined"
                          />
                        </Box>
                        
                        <Typography variant="body2" color="text.secondary">
                          {rdv.veterinaire_nom || `Dr. Vétérinaire #${rdv.veterinaire}`} - {rdv.animal_nom || `Animal #${rdv.animal}`}
                        </Typography>
                        
                        <Typography variant="body2" color="text.secondary">
                          {rdv.motif}
                        </Typography>
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            </Box>
          )}
        </Stack>
      )}

      {/* FAB pour prendre un rendez-vous */}
      {onPrendreRendezVous && (
        <Fab
          color="primary"
          aria-label="Prendre un rendez-vous"
          onClick={onPrendreRendezVous}
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
          }}
        >
          <AddIcon />
        </Fab>
      )}

      {/* Dialog de confirmation d'annulation */}
      <Dialog
        open={cancelDialog.open}
        onClose={() => setCancelDialog({ open: false, rdv: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Annuler le rendez-vous
        </DialogTitle>
        <DialogContent>
          {cancelDialog.rdv && (
            <Box>
              <Typography gutterBottom>
                Êtes-vous sûr de vouloir annuler ce rendez-vous ?
              </Typography>
              <Box mt={2} p={2} bgcolor="grey.50" borderRadius={1}>
                <Typography variant="body2">
                  <strong>Date :</strong> {formatDate(cancelDialog.rdv.date_heure)}
                </Typography>
                <Typography variant="body2">
                  <strong>Heure :</strong> {formatTime(cancelDialog.rdv.date_heure)}
                </Typography>
                <Typography variant="body2">
                  <strong>Vétérinaire :</strong> {cancelDialog.rdv.veterinaire_nom}
                </Typography>
                <Typography variant="body2">
                  <strong>Motif :</strong> {cancelDialog.rdv.motif}
                </Typography>
              </Box>
              <Alert severity="warning" sx={{ mt: 2 }}>
                Cette action est irréversible. Vous devrez reprendre un nouveau rendez-vous si nécessaire.
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setCancelDialog({ open: false, rdv: null })}
            disabled={cancelling}
          >
            Garder le rendez-vous
          </Button>
          <Button
            onClick={handleCancelRendezVous}
            color="error"
            variant="contained"
            disabled={cancelling}
          >
            {cancelling ? <CircularProgress size={20} /> : 'Annuler le rendez-vous'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MesRendezVous; 